import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit, ViewChild } from '@angular/core';
import { API, TemplateDataSetEntryDTO, TemplateSourceDTO, TemplateSourceEntryDTO, TemplateVariableType } from '../../../services/api/api';
import { UtilService } from '../../../services/util.service';
import { ActivatedRoute, Params } from '@angular/router';
import { ErrorService } from '../../../services/error.service';
import { SbDateTime, SbLocalDateTime } from '../../../services/api/api-date-time';
import { QueryParamService } from '../../../services/lists/query-params';
import { SbSplitButton } from '../../../services/global/global-interfaces';
import { PermissionService } from '../../../services/permission.service';
import { ModalService } from '../../../services/gui/modal/modal.service';
import { TranslateService } from '@ngx-translate/core';
import { finalize, take } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { StringTransformationService } from '../../../services/transformation-helper/string-transformation.service';
import { RequestClass } from '../../../services/global/RequestClass';
import { SbSplitButtonItem, SearchOptions } from '@ngx-seatback/controls';
import { FilterPanelComponent, FilterPanelConfig } from '@ngx-seatback/layout';
import { RoutingID } from '../../../routes/sb-routing-enum';

type SplitButtonAction = 'edit' | 'delete';

@Component({
  selector: 'sb-page-template-source-entries',
  templateUrl: './template-source-entries.component.html',
  styleUrls: ['./template-source-entries.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TemplateSourceEntriesComponent extends RequestClass implements OnInit {
  public readonly util = inject(UtilService);
  public readonly route = inject(ActivatedRoute);
  public readonly errorHandler = inject(ErrorService);
  public readonly queryParamService = inject(QueryParamService);
  public readonly permissionService = inject(PermissionService);
  protected readonly TemplateVariableType = TemplateVariableType;
  private readonly api = inject(API);
  private readonly modal = inject(ModalService);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly stringTransformation = inject(StringTransformationService);

  @ViewChild('FilterPanelRef') filterPanelRef: FilterPanelComponent;

  // table wrapper variables
  templateEntries: TemplateSourceEntryDTO[] = [];
  templateSource: TemplateSourceDTO = null;

  // filter variables
  queryParamsParsed: boolean = false;
  hasDevices: boolean = undefined;
  exactMatch: SearchOptions = 'contains';
  search: string | null = null;
  filterConfig: FilterPanelConfig = {
    showExactMatch: true,
    showSearchHeadline: true,
    button: { visibleWithoutChanges: true },
  };
  from: SbLocalDateTime | undefined = undefined;
  till: SbLocalDateTime | undefined = undefined;

  ngOnInit(): void {
    this.route.params.subscribe((params: Params) => {
      const sourceID = +params.templateSourceID;
      if (sourceID != null) {
        this.api
          .mm_templating_provider_TemplateSource_retrieveById(sourceID)
          .pipe(take(1), takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: (source: TemplateSourceDTO) => {
              this.templateSource = source;
            },
          });
      }
    });
  }

  deleteTemplateEntry(data: { id: number }) {
    this.modal
      .confirm({
        title: this.translate.instant('lang.globals.templateEntry.confirmDelete.headline'),
        message: this.translate.instant('lang.globals.templateEntry.confirmDelete.text'),
        no: this.translate.instant('lang.globals.cancel'),
        yes: this.translate.instant('lang.globals.delete'),
      })
      .then(result => {
        if (result) {
          this.api
            .mm_templating_provider_TemplateSourceEntry_deleteInSource(this.templateSource.id, data.id)
            .pipe(
              take(1),
              finalize(() => {
                this.requestDebounced();
              })
            )
            .subscribe({
              next: () => {},
              error: error => {
                this.errorHandler.errorHandler(error);
              },
            });
        }
      });
  }

  refreshFromServer() {
    if (!this.pagination || !this.templateSource?.id) {
      this.loading.set(false);
      return;
    }
    const sub = this.api
      .mm_templating_provider_TemplateSourceEntry_retrieveAllInSource(
        this.templateSource.id,
        {
          search: this.stringTransformation.encodeSearchQueryParamWithOptions(this.exactMatch, this.search),
          validFrom: this.from,
          validTill: this.till,
        },
        this.pagination
      )
      .pipe(
        take(1),
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.loading.set(false);
        })
      )
      .subscribe({
        next: (templateEntries: TemplateSourceEntryDTO[]) => {
          this.templateEntries = this.buildSplitButtons(templateEntries);
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
    this.subs.push(sub);
  }

  handleDateChange(dates: { fromDate: SbDateTime; toDate: SbDateTime }) {
    if (!dates) {
      this.from = null;
      this.till = null;
    }
    if (!dates?.fromDate || !dates?.toDate) {
      return;
    }
    this.from = new SbLocalDateTime(dates.fromDate.value);
    this.till = new SbLocalDateTime(dates.toDate.value);
    this.requestDebounced();
  }

  buildSplitButtons<T extends { id: any }>(itemsToAdd: T[]): (T & SbSplitButton<SplitButtonAction>)[] {
    const result = itemsToAdd.map(item => {
      return { ...item, sbButtons: [] as SbSplitButtonItem<SplitButtonAction>[] };
    });
    result.forEach(item => {
      if (this.permissionService.hasPermissions([this.permissionService.Permission.template_source_entry_create])) {
        item.sbButtons.push({
          icon: 'edit',
          tooltip: 'lang.globals.edit',
          link: RoutingID.templateSourceEntryEdit,
          pathParams: [{ id: 'templateSourceEntryID', value: item?.id }],
          queryParams: { templateGroupID: this.templateSource?.templateGroup?.id, templateSourceID: this.templateSource?.id },
        });
      }
      if (this.permissionService.hasPermissions([this.permissionService.Permission.template_source_entry_delete])) {
        item.sbButtons.push({
          icon: 'trash',
          iconClass: 'hover:!tw-bg-sbRed-500 *:tw-text-sbRed-500',
          action: 'delete',
          tooltip: 'lang.globals.delete',
        });
      }
    });
    return result;
  }

  resolveSplitButtonAction(entry: TemplateDataSetEntryDTO, $event: SplitButtonAction) {
    switch ($event) {
      case 'delete':
        this.deleteTemplateEntry(entry);
        break;
      default:
        console.error('Missing action implementation of Splitbutton');
        break;
    }
  }

  openFilterPanel(elementId: HTMLDivElement) {
    if (!elementId) {
      throw new Error('Missing elementId for filter panel opening');
    }
    if (this.filterPanelRef) {
      this.filterPanelRef.isPanelExtended.set(true);
      this.scrollToFilterPanelElement(elementId, false);
    }
  }

  scrollToFilterPanelElement(element: HTMLDivElement, isInitiallyExpanded: boolean = true) {
    if (!element) {
      throw new Error('Missing elementId for filter panel opening');
    }
    const timeout = setTimeout(
      () => {
        const scrollContainer = element.closest('.custom-scrollbar') as HTMLElement;

        if (!scrollContainer) {
          console.warn('No scrollable container found for element');
          return;
        }
        const containerTop = scrollContainer.getBoundingClientRect().top;
        const elementTop = element.getBoundingClientRect().top;

        const headerOffset = 110; // height filter header + height of navbar + offset
        const offsetPosition = elementTop - containerTop + scrollContainer.scrollTop - headerOffset;

        scrollContainer.scrollTo({ behavior: 'smooth', top: offsetPosition });
        // elementId.scrollBy({ top: 40 });
        clearTimeout(timeout);
      },
      isInitiallyExpanded ? 100 : 400
    );
  }

  checkExpansion() {
    return this.filterPanelRef?.isPanelExtended() ?? false;
  }

  applyFiltering(filterMap: Map<string, any[]>) {
    this.setRequestOptions(filterMap);
    this.requestDebounced();
  }

  setRequestOptions(filterMap: Map<string, any[]>) {
    this.search = filterMap.get('search')?.[0];
    this.exactMatch = filterMap.get('exactMatch')?.[0];
  }
}
